import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, serverTimestamp, query, orderBy, updateDoc } from "firebase/firestore";
import { FiTag, FiPlus, FiTrash2, FiX, FiCheck, FiCalendar, FiSearch, FiLayers, FiEdit2 } from "react-icons/fi";
import toast from "react-hot-toast";

export default function AdminDiscounts() {
    const [coupons, setCoupons] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [editingId, setEditingId] = useState(null);

    const [form, setForm] = useState({
        code: "",
        type: "percentage", 
        value: "",
        minOrderAmount: "0",
        usageLimit: "",
        startDate: "",
        endDate: "",
        applicableItems: [], // empty = all
        isActive: true
    });

    const resetForm = () => {
        setForm({ code: "", type: "percentage", value: "", minOrderAmount: "0", usageLimit: "", startDate: "", endDate: "", applicableItems: [], isActive: true });
        setEditingId(null);
        setSearch("");
    };

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        setLoading(true);
        try {
            const [cSnap, pSnap] = await Promise.all([
                getDocs(query(collection(db, "coupons"), orderBy("createdAt", "desc"))),
                getDocs(query(collection(db, "products"), orderBy("title")))
            ]);
            setCoupons(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            setProducts(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
            toast.error("Failed to load discounts.");
        }
        setLoading(false);
    }

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    }

    function toggleItem(id) {
        setForm(f => ({
            ...f,
            applicableItems: f.applicableItems.includes(id)
                ? f.applicableItems.filter(x => x !== id)
                : [...f.applicableItems, id]
        }));
    }

    async function handleSave() {
        if (!form.code || !form.value) { toast.error("Code and value are required."); return; }
        setSaving(true);
        try {
            const payload = {
                ...form,
                code: form.code.toUpperCase().trim(),
                value: parseFloat(form.value),
                minOrderAmount: parseFloat(form.minOrderAmount || 0),
                usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
                updatedAt: serverTimestamp()
            };

            if (editingId) {
                await updateDoc(doc(db, "coupons", editingId), payload);
                toast.success("Coupon updated!");
            } else {
                await addDoc(collection(db, "coupons"), {
                    ...payload,
                    usedCount: 0,
                    createdAt: serverTimestamp()
                });
                toast.success("Coupon created!");
            }
            
            setModal(false);
            resetForm();
            loadData();
        } catch (e) {
            console.error(e);
            toast.error("Failed to save.");
        }
        setSaving(false);
    }

    function handleEdit(coupon) {
        setEditingId(coupon.id);
        setForm({
            code: coupon.code,
            type: coupon.type,
            value: coupon.value.toString(),
            minOrderAmount: coupon.minOrderAmount.toString(),
            usageLimit: coupon.usageLimit ? coupon.usageLimit.toString() : "",
            startDate: coupon.startDate || "",
            endDate: coupon.endDate || "",
            applicableItems: coupon.applicableItems || [],
            isActive: coupon.isActive
        });
        setModal(true);
    }

    async function handleDelete(id) {
        if (!window.confirm("Delete this coupon?")) return;
        try {
            await deleteDoc(doc(db, "coupons", id));
            toast.success("Deleted.");
            loadData();
        } catch {
            toast.error("Delete failed.");
        }
    }

    const filteredProducts = products.filter(p => (p.title || "").toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="admin-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 30 }}>
                <div>
                    <h2 style={{ margin: 0 }}>Discount Coupons</h2>
                    <p style={{ color: "#888", margin: "4px 0 0", fontSize: 14 }}>Manage promotional codes and limited-time offers.</p>
                </div>
                <button className="admin-btn-primary" onClick={() => { resetForm(); setModal(true); }}>
                    <FiPlus /> New Coupon
                </button>
            </div>

            {loading ? <p>Loading coupons...</p> : (
                <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Discount</th>
                                <th>Requirements</th>
                                <th>Validity</th>
                                <th>Usage</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {coupons.length === 0 && (
                                <tr><td colSpan="7" style={{ textAlign: "center", padding: 40, color: "#888" }}>No coupons created yet.</td></tr>
                            )}
                            {coupons.map(c => (
                                <tr key={c.id}>
                                    <td><strong style={{ letterSpacing: 1, color: "var(--accent)" }}>{c.code}</strong></td>
                                    <td>
                                        {c.type === "percentage" ? `${c.value}% OFF` : `$${c.value} OFF`}
                                        <div style={{ fontSize: 11, color: "#888" }}>
                                            {c.applicableItems.length === 0 ? "Site-wide" : `${c.applicableItems.length} specific items`}
                                        </div>
                                    </td>
                                    <td>
                                        {c.minOrderAmount > 0 ? `Min. $${c.minOrderAmount}` : "None"}
                                    </td>
                                    <td>
                                        <div style={{ fontSize: 12 }}>
                                            {c.startDate ? new Date(c.startDate).toLocaleDateString() : "Anytime"}
                                            {" — "}
                                            {c.endDate ? new Date(c.endDate).toLocaleDateString() : "No end"}
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ fontSize: 12 }}>
                                            {c.usedCount} / {c.usageLimit || "∞"}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${c.isActive ? "paid" : "physical"}`} style={{ padding: "4px 10px" }}>
                                            {c.isActive ? "Active" : "Disabled"}
                                        </span>
                                    </td>
                                     <td>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button className="admin-btn-secondary" onClick={() => handleEdit(c)} style={{ padding: "6px 10px" }}>
                                                <FiEdit2 size={14} />
                                            </button>
                                            <button className="admin-btn-danger" onClick={() => handleDelete(c.id)} style={{ padding: "6px 10px" }}>
                                                <FiTrash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </div>
            )}

            {modal && (
                <div className="admin-modal-backdrop" onClick={e => e.target === e.currentTarget && setModal(false)}>
                    <div className="admin-modal" style={{ maxWidth: 700 }}>
                        <div className="admin-modal-header">
                            <h3>{editingId ? "Edit Coupon" : "Create New Coupon"}</h3>
                            <button className="admin-modal-close" onClick={() => setModal(false)}><FiX size={20} /></button>
                        </div>
                        <div className="admin-modal-body">
                            <div className="admin-form-grid">
                                <div className="admin-form-field">
                                    <label>Coupon Code *</label>
                                    <input name="code" value={form.code} onChange={handleChange} placeholder="SUMMER25" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Discount Type</label>
                                    <select name="type" value={form.type} onChange={handleChange}>
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="fixed">Fixed Amount ($)</option>
                                    </select>
                                </div>
                                <div className="admin-form-field">
                                    <label>Discount Value *</label>
                                    <input name="value" type="number" value={form.value} onChange={handleChange} placeholder={form.type === "percentage" ? "15" : "10.00"} />
                                </div>
                                <div className="admin-form-field">
                                    <label>Min. Order Amount ($)</label>
                                    <input name="minOrderAmount" type="number" value={form.minOrderAmount} onChange={handleChange} placeholder="0" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Usage Limit (leave empty for unlimited)</label>
                                    <input name="usageLimit" type="number" value={form.usageLimit} onChange={handleChange} placeholder="100" />
                                </div>
                                <div className="admin-form-field">
                                    <label>Status</label>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                                        <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                                        <span>Active and redeemable</span>
                                    </div>
                                </div>
                                <div className="admin-form-field">
                                    <label><FiCalendar size={13} /> Start Date</label>
                                    <input name="startDate" type="date" value={form.startDate} onChange={handleChange} />
                                </div>
                                <div className="admin-form-field">
                                    <label><FiCalendar size={13} /> End Date</label>
                                    <input name="endDate" type="date" value={form.endDate} onChange={handleChange} />
                                </div>

                                <div className="admin-form-field full">
                                    <label style={{ display: "flex", justifyContent: "space-between" }}>
                                        <span>Applicable Products (All if none selected)</span>
                                        <span style={{ fontWeight: 400, color: "#888" }}>{form.applicableItems.length} selected</span>
                                    </label>
                                    <div style={{ position: "relative", marginBottom: 12 }}>
                                        <FiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#aaa" }} />
                                        <input 
                                            value={search} 
                                            onChange={e => setSearch(e.target.value)} 
                                            placeholder="Filter products..." 
                                            style={{ paddingLeft: 36, fontSize: 13 }}
                                        />
                                    </div>
                                    <div style={{ maxHeight: 200, overflowY: "auto", border: "1.5px solid #eee", borderRadius: 8, padding: 8 }}>
                                        {filteredProducts.map(p => (
                                            <div 
                                                key={p.id} 
                                                onClick={() => toggleItem(p.id)}
                                                style={{ 
                                                    display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 6, cursor: "pointer",
                                                    background: form.applicableItems.includes(p.id) ? "#f0faf5" : "transparent",
                                                    marginBottom: 4
                                                }}
                                            >
                                                <div style={{ 
                                                    width: 18, height: 18, borderRadius: 4, border: "1.5px solid", 
                                                    borderColor: form.applicableItems.includes(p.id) ? "var(--green-primary)" : "#ccc",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    background: form.applicableItems.includes(p.id) ? "var(--green-primary)" : "white"
                                                }}>
                                                    {form.applicableItems.includes(p.id) && <FiCheck size={12} color="white" />}
                                                </div>
                                                <span style={{ fontSize: 13, fontWeight: 500 }}>{p.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                            <button className="admin-btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? "Saving..." : (editingId ? "Update Coupon" : "Create Coupon")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
